import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'

// The header bell: icon → dot when something arrived since the account's
// read marker → panel with the list → modal with the full text.
//
// READ STATE IS ONE TIMESTAMP ON THE ACCOUNT (`users.read_at`), Timotei's
// rule. There is no per-message read flag, opening a message does NOT mark it
// read, and nothing ever makes a message unread again. Only the "mark all
// read" button moves the marker, and it moves it to the newest message's
// timestamp rather than to now() — the reasoning is in sql/001_users.sql.
//
// The modal is the ONE agreed exception to "all forms are standalone pages":
// it is a reader, not a form, and bouncing to a URL and back would lose the
// admin's place in the list.
//
// Polling is deliberately lazy: once on mount and then every five minutes,
// paused entirely while the tab is hidden. An announcement is not a chat
// message — nobody needs it within the second, and a per-user query on every
// open tab all day is exactly the cost this feature does not need to pay.

const POLL_MS = 5 * 60 * 1000

export function AnnouncementsBell() {
  const { t, i18n } = useTranslation()
  const [data, setData] = useState(null)     // null = never loaded
  const [open, setOpen] = useState(false)
  const [reading, setReading] = useState(null) // the item shown in the modal
  const [busy, setBusy] = useState(false)
  const wrapRef = useRef(null)

  const load = useCallback(() => {
    api.announcements.list()
      .then(setData)
      .catch(() => { /* not signed in / offline — the bell just stays quiet */ })
  }, [])

  useEffect(() => { load() }, [load])

  // Re-fetch when the language changes: subject and body come back already
  // picked in the user's language by the server, so the cached copy is stale.
  useEffect(() => { load() }, [i18n.language, load])

  useEffect(() => {
    const tick = () => { if (!document.hidden) load() }
    const id = setInterval(tick, POLL_MS)
    document.addEventListener('visibilitychange', tick)
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick) }
  }, [load])

  // Click-outside / Escape close the panel. The modal handles its own Escape
  // (it is on top), so this only fires when no message is open.
  useEffect(() => {
    if (!open) return
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    const onKey  = (e) => { if (e.key === 'Escape' && !reading) setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open, reading])

  async function markAllRead() {
    setBusy(true)
    try {
      await api.announcements.readAll()
      load()
    } catch { /* keep the dot rather than lying about it */ }
    finally { setBusy(false) }
  }

  const items  = data?.items || []
  const unread = data?.unread || 0
  const hasNew = !!data?.has_new

  function togglePanel() {
    const next = !open
    setOpen(next)
    if (next) load() // opening is the one moment a fresh read is worth it
  }

  const fmtDate = (s) => {
    if (!s) return ''
    const d = new Date(String(s).replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return s
    return d.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'ro-RO', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  }

  return (
    <span className="bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className="bell-button"
        onClick={togglePanel}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={hasNew
          ? t('announcements.bell_label_new', { count: unread })
          : t('announcements.bell_label')}
        title={t('announcements.title')}
      >
        <span aria-hidden="true">🔔</span>
        {hasNew && <span className="bell-dot" aria-hidden="true" />}
      </button>

      {open && (
        <div className="bell-panel" role="menu">
          <div className="bell-panel-head">
            <strong>{t('announcements.title')}</strong>
            {hasNew && (
              <button type="button" className="link-button" disabled={busy} onClick={markAllRead}>
                {busy ? t('common.loading') : t('announcements.mark_all_read')}
              </button>
            )}
          </div>

          {data === null && <p className="muted bell-empty">{t('common.loading')}</p>}

          {data !== null && items.length === 0 && (
            <p className="muted bell-empty">{t('announcements.empty')}</p>
          )}

          {items.length > 0 && (
            <ul className="bell-list">
              {items.map((it) => (
                <li key={it.id} className={it.is_new ? 'is-new' : ''}>
                  <button type="button" className="bell-item" onClick={() => setReading(it)}>
                    <span className="bell-item-subject">
                      {it.is_new && <span className="bell-item-dot" aria-hidden="true" />}
                      {it.subject}
                    </span>
                    <span className="muted small">{fmtDate(it.received_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {reading && (
        <AnnouncementModal item={reading} onClose={() => setReading(null)} fmtDate={fmtDate} />
      )}
    </span>
  )
}

// The reader. `body` was sanitized server-side at WRITE time against a short
// tag whitelist (AnnouncementContent::sanitizeHtml), which is why setting it
// as innerHTML here is safe — and why sanitizing on write rather than on read
// was the right call: it happens once, by the only code that can be sure what
// the admin actually typed.
function AnnouncementModal({ item, onClose, fmtDate }) {
  const { t } = useTranslation()

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <section className="card">
          <h2>{item.subject}</h2>
          <p className="muted small">{fmtDate(item.received_at)}</p>
          <div
            className="announcement-body"
            dangerouslySetInnerHTML={{ __html: item.body || '' }}
          />
          <div className="form-actions">
            <button type="button" onClick={onClose}>{t('common.close')}</button>
          </div>
        </section>
      </div>
    </div>
  )
}
